import { t } from '@lingui/macro';
import { ReactImageGalleryItem } from 'react-image-gallery';
import {
  PagedRequestModel,
  SiteAlarmsMicrologModel,
  SiteAlarmsStandardModel,
  SiteAlarmsStandardFormModel,
  SiteAlarmsMicrologFormModel,
} from '.';

export enum FilterType {
  All = 'All',
  Selected = 'Selected',
}

export enum SortOrderType {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export const SortOrderOptionsFn = (): Record<SortOrderType, string> => ({
  [SortOrderType.Ascending]: t`Ascending`,
  [SortOrderType.Descending]: t`Descending`,
});

export enum SortColumnType {
  SiteId = 'SiteId',
  Location = 'Location',
  LastCallIn = 'LastCallIn',
}

export const SortColumnOptionsFn = (): Record<SortColumnType, string> => ({
  [SortColumnType.SiteId]: t`Site ID`,
  [SortColumnType.Location]: t`Address`,
  [SortColumnType.LastCallIn]: t`Last Call In`,
});

export enum SearchType {
  All = 'All',
  SiteId = 'SiteId',
  Address = 'Address',
  Serial = 'Serial',
  SMS = 'SMS',
}

export const SearchTypeOptionsFn = (allowAll?: boolean): Partial<Record<SearchType, string>> => ({
  ...(allowAll ? { [SearchType.All]: t`All` } : {}),
  [SearchType.SiteId]: t`Site ID`,
  [SearchType.Address]: t`Address`,
  [SearchType.Serial]: t`Serial`,
  [SearchType.SMS]: t`SMS`,
});

export interface SortModel {
  sortOrder: SortOrderType;
  columnSort: SortColumnType;
}

export interface SiteSummaryPagedRequest extends PagedRequestModel, SortModel {
  accountId?: number;
  search?: string;
  filterType?: FilterType;
  searchType?: SearchType;
  zone?: string;
}

export interface SiteSummaryModel {
  id: number;
  siteID?: string;
  address?: string;
  latitude: number;
  longitude: number;
  isSelected: boolean;
  device?: DeviceSummaryModel;
}

export interface DeviceSummaryModel {
  id: number;
  smsNumber?: string;
  serialNumber?: string;
  model?: string;
  version?: string;
  lastCallIn?: string;
  lastRestart?: string;
  csq: number;
  ecl?: number;
  rsrp?: number;
  rsrq?: number;
  battery?: string;
  currentMast?: MastSummaryModel;
}

export interface MastSummaryModel {
  netId: number;
  lac: number;
  cellId: number;
  latitude: number;
  longitude: number;
}

// selected sites list

export interface SelectedSitesListModel {
  id: string;
  name: string;
}

// - - - SITE INFORMATION - - -

export enum SiteInfoPageEnum {
  Site,
  Alarms,
  Channels,
  Commands,
}

// site
export interface SiteDetailsFormPostModel {
  siteID: string;
  zone?: string;
  address: string;
  note: string;
  latEast: string;
  longNorth: string;
  heightAod: number;
}

export interface GPSCoordinatesModel {
  latitude: number;
  longitude: number;
}

export interface OriginCoordinatesModel {
  latEast: string;
  longNorth: string;
}

export interface SiteDetailsFormModel extends SiteDetailsFormPostModel, GPSCoordinatesModel, OriginCoordinatesModel {
  id: number;
  owner: string;
  smsNumber: string;
  serialNumber: string;
  sampleRate: string;
  callFrequency: string;
  lastRestart?: string;
}

export interface SiteDetailsModel
  extends Omit<SiteDetailsFormModel, 'latitude' | 'longitude' | 'latEast' | 'longNorth'> {
  location: {
    gps: GPSCoordinatesModel;
    origin: OriginCoordinatesModel;
  };
}

// channels
export interface SiteChannelsFormModel {
  channels: SiteChannelModel[];
}

export interface SiteChannelsFormRequestModel {
  channels: SiteChannelRequestModel[];
}

export interface SiteChannelModel {
  id: number;
  number: number;
  type: string;
  name: string;
  lastValue: number;
  calibration: number;
  offset: number;
  useLoggerCalibrationValues: boolean;
  loggerCalibration: number;
  canSupport420: boolean;
  isFourTwenty: boolean;
  hasMeter: boolean;
  meter: SiteChannelMeterModel;
}

export interface SiteChannelMeterModel {
  meterValue: number;
  meterUnit: string;
  meterReadDate: string; // date
}

export interface SiteChannelMeterRequestModel {
  meterValue: string;
  meterTime: string; // date
}

export interface SiteChannelRequestModel {
  id: number;
  name: string;
  calibration: number;
  offset: number;
  useLoggerValues: boolean;
  is420: boolean;
}

export enum AlarmType {
  Unsupported = 'Unsupported',
  Binary = 'Binary',
  Unilog = 'Unilog',
  Server = 'Server',
  Microlog = 'Microlog',
  NoDevice = 'NoDevice',
}

export const AlarmTypeOptionsFn = (): Record<AlarmType, string> => ({
  [AlarmType.Unsupported]: t`Unsupported`,
  [AlarmType.Binary]: t`Binary`,
  [AlarmType.Unilog]: t`Unilog`,
  [AlarmType.Server]: t`Server`,
  [AlarmType.Microlog]: t`Microlog`,
  [AlarmType.NoDevice]: t`NoDevice`,
});

// new (standard) alarms - form
export interface SiteAlarmConfigurationResponseModel {
  alarmType: AlarmType;
  /**
   * Max number of conditions - do not use for Microlog
   */
  maxConditions: number;
  /**
   * Max number of persistence options - do not use for Microlog
   */
  maxPersistence: number;
  standard?: SiteAlarmsStandardModel;
  microlog?: SiteAlarmsMicrologModel;
  hasPendingAlarmCommand?: boolean;
}

export interface SiteAlarmConfigurationFormModel
  extends Omit<SiteAlarmConfigurationResponseModel, 'standard' | 'microlog'>,
    SiteAlarmsStandardFormModel,
    SiteAlarmsMicrologFormModel {}

// - - - SITE PHOTOS - - -

export interface ISitePhoto {
  id?: number;
  description?: string;
  url?: string;
}

export interface IAppGalleryPhoto extends ReactImageGalleryItem {
  id: string;
}

export enum DeviceCommandStatus {
  Requested = 'Requested',
  Queued = 'Queued',
  Accepted = 'Accepted',
  CancellationRequested = 'CancellationRequested',
  Cancelled = 'Cancelled',
  Failed = 'Failed',
  CsumFailed = 'CsumFailed',
  ProcessingFailed = 'ProcessingFailed',
  ImeiFailed = 'ImeiFailed',
  Expired = 'Expired',
}

export interface DeviceCommand {
  id?: number;
  userId?: number;
  application?: string;
  smsNumber?: string;
  commandType?: string;
  requestDate?: Date;
  sentDate?: Date;
  updateDate?: Date;
  body?: string;
  values?: string;
  status?: DeviceCommandStatus;
}

export interface CommandsParamsModel extends PagedRequestModel {
  orderBy?: string;
}
